import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useGetOrganizations from 'containers/organizations/useGetOrganizations'
import useGetCurrencies from 'containers/currencies/useGetCurrencies'
import InputField from 'components/InputField'
import DropDownField from 'components/DropDownField'
import ScreenLoading from 'components/ScreenLoading'
import Divider from 'components/Divider'
import api from 'containers/api'
import { IOrganizationCurrency } from 'types'

// Settings -> Administration -> Organization: the name and the reporting currency. Editing is
// admin-only (PATCH /organizations/{id} returns 403 otherwise); members see the values read-only.
// A currency change re-converts the warehouse and re-points Power BI after commit; the outcome
// of the last change is shown below the form (GET /organizations/{id}/currency).
const OrganizationTab = () => {
  const { currentOrganization, updateCurrentOrganization } = useContext(OrganizationsContext)
  const { clearOrganizationsCache, fetchOrganizations } = useGetOrganizations()
  const token = useSelector(selectToken)
  const [currencies, fetchCurrencies] = useGetCurrencies()
  const [name, setName] = useState(currentOrganization?.name || '')
  const [currency, setCurrency] = useState(currentOrganization?.currency_id || '')
  const [currencyInfo, setCurrencyInfo] = useState<IOrganizationCurrency | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [pendingRequest, setPendingRequest] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState('')
  const [error, setError] = useState('')

  const isAdmin = !!currentOrganization?.is_admin

  useEffect(() => {
    fetchCurrencies()
    // eslint-disable-next-line
  }, [])

  // another organization became active: leave edit mode (a half-edited form must never be saved
  // to the new organization) and load its values
  useEffect(() => {
    setIsEditing(false)
    setError('')
    setSaveSuccess('')
    setName(currentOrganization?.name || '')
    setCurrency(currentOrganization?.currency_id || '')
    setCurrencyInfo(null)
    if (currentOrganization?.id) {
      loadCurrencyInfo()
    }
    // eslint-disable-next-line
  }, [currentOrganization?.id])

  // the same organization changed under the tab (a switch made from the mismatch prompt while
  // this tab is mounted): re-sync the form unless the user is mid-edit, and refresh the status
  useEffect(() => {
    if (!isEditing) {
      setName(currentOrganization?.name || '')
      setCurrency(currentOrganization?.currency_id || '')
    }
    if (currentOrganization?.id) {
      loadCurrencyInfo()
    }
    // eslint-disable-next-line
  }, [currentOrganization?.currency_id, currentOrganization?.name])

  // a status response is applied only if it belongs to the organization that is active NOW: a
  // switch mid-poll must not paint the previous organization's currency or change status
  const activeOrgId = useRef(currentOrganization?.id)
  activeOrgId.current = currentOrganization?.id

  const loadCurrencyInfo = async (): Promise<IOrganizationCurrency | null> => {
    const requestedFor = currentOrganization?.id
    try {
      const info = await api.getOrganizationCurrency({ organizationId: requestedFor, token })
      if (requestedFor !== activeOrgId.current) return null
      setCurrencyInfo(info)
      return info
    } catch (err) {
      console.error('Error loading organization currency:', err)
      return null
    }
  }

  // the PATCH returns before the follow-ups run; poll the status until the newest change is
  // processed (the worker retries failed steps by itself; the status read also re-dispatches a
  // change a restart left behind)
  const polling = useRef<number | undefined>(undefined)   // the organization a poll is running for
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  // every 2 s for ~90 s (past the worker's first self-retry), then every 15 s for as long as the change
  // is pending and this organization stays active in a mounted tab — a later retry (backoff grows)
  // must still turn "applying" into the outcome without a reload
  const waitForLastChange = async () => {
    const forOrg = currentOrganization?.id
    if (!forOrg || polling.current === forOrg) return   // one poll per organization; it reads the newest change anyway
    polling.current = forOrg                            // a poll left over from another organization exits on its next check
    try {
      for (let i = 0; mounted.current && polling.current === forOrg; i++) {
        if (forOrg !== activeOrgId.current) return      // the user moved to another organization
        const info = await loadCurrencyInfo()
        if (info?.last_change?.processed_at || info?.last_change?.etl_triggered) return   // scheduled: what is left is internal
        await new Promise((resolve) => setTimeout(resolve, i < 45 ? 2000 : 15000))
      }
    } finally {
      if (polling.current === forOrg) polling.current = undefined
    }
  }

  // a change still being applied when the tab opens (or made elsewhere) is polled the same way as
  // one saved here, so "applying" turns into the outcome without a reload
  useEffect(() => {
    const change = currencyInfo?.last_change
    if (change && !change.processed_at && !change.etl_triggered) waitForLastChange()
    // eslint-disable-next-line
  }, [currencyInfo])

  const handleSave = async () => {
    if (!currentOrganization?.id) return
    setPendingRequest(true)
    setSaveSuccess('')
    setError('')
    try {
      if (!name.trim()) {
        setError('The organization name cannot be blank.')
        setPendingRequest(false)
        return
      }
      const changes: { name?: string, currency?: string } = {}
      if (name.trim() !== currentOrganization.name) changes.name = name.trim()
      if (currency && currency !== currentOrganization.currency_id) changes.currency = currency
      const savedFor = currentOrganization.id
      if (Object.keys(changes).length > 0) {
        const saved = await api.updateOrganization({ organizationId: savedFor, token, ...changes })
        // the route's active organization first (what this tab and the mismatch prompt read), then the cache
        if (updateCurrentOrganization) updateCurrentOrganization(saved)
        await clearOrganizationsCache()
        await fetchOrganizations()
      }
      if (savedFor !== activeOrgId.current) return   // the user moved on: no message or edit-mode change for another organization
      setSaveSuccess(changes.currency
        ? `Saved. Your figures are being recalculated in ${changes.currency}; dashboards update after the next data refresh.`
        : 'Organization settings updated successfully')
      setIsEditing(false)
      setPendingRequest(false)   // Save is done; the follow-up status is polled without blocking the form
      if (changes.currency) waitForLastChange()
      else loadCurrencyInfo()
    } catch (err: any) {
      console.error('Error updating organization:', err)
      if (currentOrganization.id !== activeOrgId.current) return   // same: a late failure is not the new organization's
      const detail = err?.response?.data?.detail
      setError(detail?.type === 'not_admin'
        ? 'Only organization administrators can change these settings.'
        : detail?.type === 'name_already_exists'
          ? 'An organization with this name already exists.'
          : 'Failed to update organization. Please try again.')
    } finally {
      setPendingRequest(false)
    }
  }

  const handleCancel = () => {
    setName(currentOrganization?.name || '')
    setCurrency(currentOrganization?.currency_id || '')
    setIsEditing(false)
    setError('')
    setSaveSuccess('')
  }

  const currencyOptions = (currencies || []).map((c) => ({ label: `${c.code} — ${c.name}`, value: c.code }))
  const currencyLabel = currencyInfo
    ? `${currencyInfo.code} — ${currencyInfo.name} (${currencyInfo.symbol})`
    : currentOrganization?.currency_id
  const lastChange = currencyInfo?.last_change
  // What the admin needs to know is one thing: are their figures being recalculated. Which internal
  // step ran (the ETL trigger, the Power BI parameter push — re-done by the data run's own refresh
  // anyway — or a workspace that does not exist yet) stays in the record for support, not on screen.
  const changeStatus = !lastChange ? { color: '', text: '' }
    : lastChange.etl_triggered
      ? { color: '#28a745', text: `Applied. Your figures are recalculated in ${lastChange.to_code}; dashboards show them after the next data refresh.` }
      : !lastChange.processed_at
        ? (lastChange.attempts > 0
          ? { color: '#f0ad4e', text: 'Taking longer than usual — retrying automatically.' }
          : { color: '#9aa0a6', text: 'Applying…' })
        : { color: '#dc3545', text: 'This change could not be applied. Save it again, or contact support if it keeps failing.' }

  return (
    <div style={{ marginTop: '1rem', maxWidth: '600px', padding: '0 1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
        {isEditing ? (
          <>
            <InputField
              label="Name"
              type="text"
              value={name}
              setCurrentValue={setName}
              style={{ margin: '0' }}
            />
            <DropDownField
              label="Reporting currency"
              options={currencyOptions}
              value={currency}
              onChange={setCurrency}
              loadingOptions={!currencies || currencies.length === 0}
              searchable
              required
              helperText="All amounts in Maya — ad spend, conversion value, Shopify revenue — are shown in this currency, using the previous month's average exchange rate."
              style={{ width: '100%' }}
            />
            {currency && currency !== currentOrganization?.currency_id && (
              <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', color: '#856404', fontSize: '0.9rem', lineHeight: 1.6 }}>
                All your figures, past ones included, will be recalculated in {currency}. Dashboards keep
                showing {currentOrganization?.currency_id} until the next data refresh.
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#495057' }}>Name</label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', color: '#6c757d' }}>
                {currentOrganization?.name}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', color: '#495057' }}>Reporting currency</label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', color: '#6c757d' }}>
                {currencyLabel}
              </div>
              <small style={{ color: '#999', fontSize: '0.85rem' }}>
                All amounts in Maya are shown in this currency.
              </small>
            </div>
          </>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}
      {saveSuccess && <p className="form-success">{saveSuccess}</p>}

      <div style={{ marginTop: '1.8rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {!isEditing ? (
          isAdmin ? (
            <button type="button" className="submit-button" onClick={() => { setError(''); setSaveSuccess(''); setIsEditing(true) }} style={{ minWidth: '120px' }}>
              Edit
            </button>
          ) : (
            <small style={{ color: '#999', fontSize: '0.85rem' }}>
              Only organization administrators can change these settings.
            </small>
          )
        ) : (
          <>
            <button type="button" className="submit-button" onClick={handleSave} disabled={pendingRequest} style={{ minWidth: '120px' }}>
              {pendingRequest ? <ScreenLoading spinnerColor="white" /> : 'Save'}
            </button>
            <button type="button" className="submit-button" style={{ backgroundColor: '#6c757d', minWidth: '120px' }} onClick={handleCancel} disabled={pendingRequest}>
              Cancel
            </button>
          </>
        )}
      </div>

      {lastChange && (
        <>
          <Divider spaceY={30} />
          <div>
            <h3 style={{ marginBottom: '0.5rem' }}>Last currency change</h3>
            <p style={{ color: '#6c757d', fontSize: '0.9rem', lineHeight: 1.8, margin: 0 }}>
              {lastChange.from_code} → {lastChange.to_code} · {new Date(lastChange.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
            <p style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', fontSize: '0.9rem', lineHeight: 1.6, margin: '0.25rem 0 0', color: '#3c4043' }}>
              <span aria-hidden="true" style={{ flex: 'none', width: 8, height: 8, borderRadius: '50%', backgroundColor: changeStatus.color, transform: 'translateY(-1px)' }} />
              <span>{changeStatus.text}</span>
            </p>
          </div>
        </>
      )}
    </div>
  )
}

export default OrganizationTab
